import { Call, Device } from "@twilio/voice-sdk";
import { Reservation, Task } from "~/packages/taskrouter";
import { VoiceCall, HoldCallOptions } from "~/modules/VoiceCall";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { TaskRouter } from "~/packages/taskrouter/TaskRouter";
import {
    canHold,
    checkWorker,
    getVoiceReservation,
    handleGetParticipantsByTask,
    isLiveCall
} from "~/modules/actions/ActionUtils";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { getLogger, Logger, LoggerName } from "~/modules/logger";
import { CbmImpl, ParticipantType } from "~/modules/cbm";
import type { Cbm } from "~/modules/cbm";
import { ConferenceRecordingServiceImpl } from "~/backend/generated/VoiceRecording/api/conferenceRecording.service";
import { ConferenceRecordingStatus } from "~/backend/generated/VoiceRecording/model/conferenceRecordingStatus";
import { PauseBehavior } from "~/backend/generated/VoiceRecording/model/pauseBehavior";
import { createFlexSdkError, throwFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";

const TWILIO_CURRENT = "Twilio.CURRENT";

export class VoiceCallImpl implements VoiceCall {
    readonly #taskRouter: TaskRouter;

    readonly #conferenceRecordingService: ConferenceRecordingServiceImpl;

    readonly #cbm: Cbm;

    readonly #ctx: ContextManager;

    readonly #logger: Logger;

    readonly #call: Call | null;

    readonly #device: Device;

    constructor(ctx: ContextManager, call: Call | null, device: Device) {
        this.#taskRouter = ctx.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        this.#conferenceRecordingService = ctx.retrieveInstanceOf(
            ContextInstance.ConferenceRecordingServiceImpl,
            ConferenceRecordingServiceImpl
        );

        this.#cbm = ctx.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        this.#ctx = ctx;
        this.#logger = getLogger(ctx)(LoggerName.Call);
        this.#logger.debug("VoiceCall constructed");
        this.#device = device;
        this.#call = call;
    }

    #validateCall(): void {
        if (this.call === null) {
            const errorMsg = "VoiceCall: Call instance is undefined. Call might be active on a different device";
            this.#logger.error(errorMsg);
            throwFlexSdkError(this.#ctx)(ErrorCode.CallInstanceUndefined, { severity: ErrorSeverity.Error }, errorMsg);
        }
    }

    get call(): Call | null {
        return this.#call;
    }

    get device(): Device {
        return this.#device;
    }

    isMuted() {
        this.#validateCall();
        return this.#call?.isMuted();
    }

    mute() {
        this.#validateCall();
        this.#call?.mute(true);
    }

    unmute() {
        this.#validateCall();
        this.#call?.mute(false);
    }

    async disconnect(): Promise<void> {
        this.#validateCall();

        try {
            this.#call?.disconnect();
            return Promise.resolve();
        } catch (error) {
            const errorMsg = `VoiceCall.disconnect: Failed to disconnect the call`;
            this.#logger.error(errorMsg, error);
            return Promise.reject(
                createFlexSdkError(this.#ctx)(
                    ErrorCode.FailedToDisconnectCall,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }
    }

    async hold(options?: HoldCallOptions): Promise<Task> {
        this.#validateCall();

        await this.#taskRouter.init();
        const worker = this.#taskRouter.worker;
        const holdMusicUrl = options?.holdMusicUrl;
        const holdMusicMethod = options?.holdMusicMethod ?? "GET";

        checkWorker(worker, {
            actionName: "VoiceCall.hold",
            logger: this.#logger,
            moduleName: "VoiceCall",
            context: this.#ctx,
            worker
        });

        const reservation = this.#getVoiceReservationOrThrow("VoiceCall.hold");

        if (!canHold(reservation, worker)) {
            const errorMsg = `VoiceCall.hold: Cannot hold reservation ${reservation.sid} because it is not an active call assigned to the worker`;
            this.#logger.error(errorMsg);
            return Promise.reject(
                createFlexSdkError(this.#ctx)(
                    ErrorCode.WorkerCannotHoldReservation,
                    { severity: ErrorSeverity.Error },
                    errorMsg
                )
            );
        }

        try {
            return await reservation?.task?.updateParticipant({
                hold: true,
                ...(holdMusicUrl && { holdUrl: holdMusicUrl }),
                holdMethod: holdMusicMethod
            });
        } catch (error) {
            const errorMsg = `VoiceCall.hold: Failed to hold call`;
            this.#logger.error(errorMsg, error);
            return Promise.reject(
                createFlexSdkError(this.#ctx)(
                    ErrorCode.FailedToHoldCall,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }
    }

    async unhold(): Promise<Task> {
        this.#validateCall();

        await this.#taskRouter.init();
        const worker = this.#taskRouter.worker;

        checkWorker(worker, {
            actionName: "VoiceCall.unhold",
            logger: this.#logger,
            moduleName: "VoiceCall",
            context: this.#ctx,
            worker
        });

        const reservation = this.#getVoiceReservationOrThrow("VoiceCall.unhold");

        if (!canHold(reservation, worker)) {
            const errorMsg = `VoiceCall.unhold: Cannot unhold reservation ${reservation.sid} because it is not an active call assigned to the worker`;
            this.#logger.error(errorMsg);
            return Promise.reject(
                createFlexSdkError(this.#ctx)(
                    ErrorCode.WorkerCannotUnholdReservation,
                    { severity: ErrorSeverity.Error },
                    errorMsg
                )
            );
        }

        try {
            return await reservation?.task?.updateParticipant({
                hold: false
            });
        } catch (error) {
            const errorMsg = `VoiceCall.unhold: Failed to unhold call`;
            this.#logger.error(errorMsg, error);
            return Promise.reject(
                createFlexSdkError(this.#ctx)(
                    ErrorCode.FailedToUnholdCall,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }
    }

    async isOnHold(): Promise<boolean> {
        this.#validateCall();

        await this.#taskRouter.init();

        const reservation = this.#getVoiceReservationOrThrow("VoiceCall.isOnHold");

        if (!isLiveCall(reservation)) {
            const errorMsg = `VoiceCall.isOnHold: Voice reservation is not a live call`;
            this.#logger.error(errorMsg);
            return Promise.reject(
                createFlexSdkError(this.#ctx)(
                    ErrorCode.VoiceReservationNotLiveCall,
                    { severity: ErrorSeverity.Error },
                    errorMsg
                )
            );
        }

        await (this.#cbm as CbmImpl).init();

        const participants = await handleGetParticipantsByTask(this.#cbm, reservation.task, {
            logger: this.#logger,
            moduleName: "VoiceCall",
            actionName: "isOnHold",
            context: this.#ctx,
            worker: this.#taskRouter.worker
        });

        return participants.some(
            (participant) =>
                participant.type === ParticipantType.Customer &&
                JSON.parse((participant.mediaProperties?.hold as string) ?? "false")
        );
    }

    async pauseRecording(pauseBehavior?: PauseBehavior): Promise<void> {
        this.#validateCall();

        await this.#taskRouter.init();
        const worker = this.#taskRouter.worker;
        checkWorker(worker, {
            actionName: "VoiceCall.pauseRecording",
            logger: this.#logger,
            moduleName: "VoiceCall",
            context: this.#ctx,
            worker
        });

        const accountSid = worker.accountSid;

        const reservation = this.#getVoiceReservationOrThrow("VoiceCall.pauseRecording");

        const conferenceSid = this.#getConferenceSidOrThrow(reservation, "VoiceCall.pauseRecording");

        try {
            await this.#conferenceRecordingService.updateConferenceRecording(
                accountSid,
                conferenceSid,
                TWILIO_CURRENT,
                {
                    Status: ConferenceRecordingStatus.Paused,
                    PauseBehavior: pauseBehavior ?? PauseBehavior.Silence
                }
            );
        } catch (error) {
            if (error instanceof Error && error.message.includes("Requested resource is not eligible for recording")) {
                const errorMsg = `VoiceCall.pauseRecording: Failed to pause the recording. There is no active recording to pause`;

                this.#logger.error(errorMsg, error);
                return Promise.reject(
                    createFlexSdkError(this.#ctx)(
                        ErrorCode.NoActiveRecordingToPause,
                        { severity: ErrorSeverity.Error },
                        errorMsg,
                        error
                    )
                );
            }
            const errorMsg = `VoiceCall.pauseRecording: Failed to pause the recording`;
            this.#logger.error(errorMsg, error);
            return Promise.reject(
                createFlexSdkError(this.#ctx)(
                    ErrorCode.FailedToPauseRecording,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }
        return Promise.resolve();
    }

    async resumeRecording(): Promise<void> {
        this.#validateCall();

        await this.#taskRouter.init();
        const worker = this.#taskRouter.worker;
        checkWorker(worker, {
            actionName: "VoiceCall.resumeRecording",
            logger: this.#logger,
            moduleName: "VoiceCall",
            context: this.#ctx,
            worker
        });

        const accountSid = worker.accountSid;

        const reservation = this.#getVoiceReservationOrThrow("VoiceCall.resumeRecording");
        const conferenceSid = this.#getConferenceSidOrThrow(reservation, "VoiceCall.resumeRecording");

        try {
            await this.#conferenceRecordingService.updateConferenceRecording(
                accountSid,
                conferenceSid,
                TWILIO_CURRENT,
                {
                    Status: ConferenceRecordingStatus.InProgress
                }
            );
        } catch (error) {
            if (error instanceof Error && error.message.includes("Requested resource is not eligible for recording")) {
                const errorMsg = `VoiceCall.resumeRecording: Failed to resume the recording. There is no active recording to resume`;
                this.#logger.error(errorMsg, error);
                return Promise.reject(
                    createFlexSdkError(this.#ctx)(
                        ErrorCode.NoActiveRecordingToResume,
                        { severity: ErrorSeverity.Error },
                        errorMsg,
                        error
                    )
                );
            }

            const errorMsg = `VoiceCall.resumeRecording: Failed to resume the recording`;
            this.#logger.error(errorMsg, error);
            return Promise.reject(
                createFlexSdkError(this.#ctx)(
                    ErrorCode.FailedToResumeRecording,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }
        return Promise.resolve();
    }

    #getVoiceReservationOrThrow(methodName: string): Reservation {
        const worker = this.#taskRouter.worker;
        const reservation = getVoiceReservation(worker);

        if (!reservation) {
            const errorMsg = `${methodName}: Voice reservation not found for worker ${
                worker?.sid as string | undefined
            }`;
            this.#logger.error(errorMsg);
            return throwFlexSdkError(this.#ctx)(
                ErrorCode.VoiceReservationNotFound,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }

        return reservation;
    }

    #getConferenceSidOrThrow(reservation: Reservation, methodName: string): string {
        const conferenceSid = (reservation?.task?.attributes?.conference as { sid?: string } | undefined)?.sid;

        if (!conferenceSid) {
            const errorMsg = `${methodName}: No Conference Sid found in task attributes. Please try again after the conference is created`;
            this.#logger.error(errorMsg);
            return throwFlexSdkError(this.#ctx)(
                ErrorCode.NoConferenceSidFoundInTaskAttributes,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }
        return conferenceSid;
    }
}
